import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import useMagicLinkVerification from '../../access/useMagicLinkVerification';
import { handleHandoffEnvelope } from './handleHandoffEnvelope';
import { buildVisibleAnswerPayload, hasPendingUploads, stripClientOnlyFields } from './answerPath';
import { normalizeRespondentAnswers } from './optionValues';
import useFormAnswers from './useFormAnswers';

const PENDING_UPLOAD_MESSAGE = 'Please wait for uploads to finish before submitting. Remove any failed uploads or retry them.';

function clientValidate(fields, answers) {
  if (!Array.isArray(fields)) return {};
  const errors = {};
  for (const field of fields) {
    if (field.type === 'display') continue;
    if (!field.required) continue;
    const val = answers?.[field.key];
    const empty =
      val === undefined ||
      val === null ||
      val === '' ||
      (Array.isArray(val) && val.length === 0);
    if (empty) {
      errors[field.key] = 'This field is required';
    }
  }
  return errors;
}

const STATUS_ERROR_MESSAGES = {
  403: 'You do not have permission to access this form.',
  404: 'This form link is invalid or has been revoked.',
  410: 'This form link has expired.',
};

function resolveErrorMessage(error, fallback) {
  const status = error?.response?.status;
  if (STATUS_ERROR_MESSAGES[status]) return STATUS_ERROR_MESSAGES[status];
  const data = error?.response?.data;
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      if (parsed?.error) return parsed.error;
    } catch { /* not JSON */ }
    return data;
  }
  if (data?.error) return data.error;
  if (data?.message) return data.message;
  return error?.message || fallback;
}

export default function usePublicForm({ adapter, resourceId, onInstanceCreated }) {
  const verifyMagicLink = useCallback(
    async (verificationToken) => adapter.verifyMagicLink(verificationToken),
    [adapter]
  );

  const { grantToken, verificationError, clearGrantToken } = useMagicLinkVerification(
    resourceId, verifyMagicLink
  );

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(null);
  const [serverValidation, setServerValidation] = useState({ errors: {}, warnings: {} });
  const [error, setError] = useState('');
  const [gateState, setGateState] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [touchedFields, setTouchedFields] = useState({});
  const [showAllValidation, setShowAllValidation] = useState(false);
  const [authPassed, setAuthPassed] = useState(false);

  const blurValidationInFlightRef = useRef(new Set());
  const lastValidatedFieldValueRef = useRef({});
  const latestBlurRequestIdRef = useRef(0);
  const instanceNotifiedRef = useRef(false);

  // Core answer/autosave/upload machinery. Everything in here is shared with useFormFlow.
  const core = useFormAnswers({ adapter, resourceId, grantToken, submitting });
  const {
    answers,
    answersRef,
    autoSaveStatus,
    lastSavedAt,
    savingDraft,
    onChangeAnswer,
    onUpload,
    onRemoveFile,
    retryUpload,
    saveDraft: coreSaveDraft,
    requestServerAutoSave,
    cancelPendingServerAutoSave,
    hydrate,
    restoreFromLocalDraft,
    setLastSavedAtFromServer,
    clearLocalBackup,
  } = core;

  useEffect(() => {
    if (verificationError) setError(verificationError);
  }, [verificationError]);

  // Bootstrap + gate state
  useEffect(() => {
    if (!resourceId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const result = await adapter.bootstrap(grantToken);
        if (cancelled) return;

        if (result.notPublished) {
          setGateState('not_published');
          setLoading(false);
          return;
        }
        if (result.disabled) {
          setGateState('disabled');
          setLoading(false);
          return;
        }
        if (result.requiresEmailVerification && !grantToken) {
          setGateState('email_required');
          setLoading(false);
          return;
        }
        if (result.requiresAuth && !authPassed) {
          setGateState('auth_required');
          setLoading(false);
          return;
        }

        setGateState(null);
        await loadForm(cancelled);
      } catch (e) {
        if (cancelled) return;
        await loadForm(cancelled);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourceId, grantToken, authPassed]);

  const loadForm = async (cancelled) => {
    try {
      const data = await adapter.loadForm(grantToken);
      if (cancelled) return;
      setForm(data);
      hydrate(normalizeRespondentAnswers(data?.fields, data?.answers || {}));
      lastValidatedFieldValueRef.current = {};
      setTouchedFields({});
      setShowAllValidation(false);
      setError('');
      setGateState(null);
      if (data?.savedAt) setLastSavedAtFromServer(data.savedAt);

      const serverAnswers = data?.answers || {};
      const hasServerAnswers = Object.keys(serverAnswers).some(
        (k) => serverAnswers[k] !== undefined && serverAnswers[k] !== null && serverAnswers[k] !== ''
      );
      if (!hasServerAnswers) restoreFromLocalDraft();
    } catch (e) {
      if (cancelled) return;
      if (e?.response?.status === 401) {
        setGateState('auth_required');
        setError('');
      } else if (e?.response?.status === 403 && grantToken) {
        clearGrantToken();
        setGateState('email_required');
        setError('');
      } else {
        setError(resolveErrorMessage(e, 'Unable to load form.'));
      }
    } finally {
      setLoading(false);
    }
  };

  const markFieldTouched = useCallback((fieldKey) => {
    setTouchedFields((prev) => {
      if (prev[fieldKey]) return prev;
      return { ...prev, [fieldKey]: true };
    });
  }, []);

  const clearFieldValidation = useCallback((fieldKey) => {
    setServerValidation((prev) => {
      const errors = { ...prev.errors };
      const warnings = { ...prev.warnings };
      delete errors[fieldKey];
      delete warnings[fieldKey];
      return { errors, warnings };
    });
  }, []);

  // Server-side validation on blur. Always fires requestServerAutoSave regardless of
  // validation capability so the draft is persisted; the adapter.validate call only runs
  // if the surface advertises server validation.
  const onFieldBlur = useCallback(async (fieldKey, nextValue) => {
    markFieldTouched(fieldKey);
    requestServerAutoSave();

    if (!adapter.capabilities.hasServerValidation || submitting) return;

    const serializedValue = JSON.stringify(nextValue ?? null);
    if (lastValidatedFieldValueRef.current[fieldKey] === serializedValue) return;
    if (blurValidationInFlightRef.current.has(fieldKey)) return;

    clearFieldValidation(fieldKey);

    blurValidationInFlightRef.current.add(fieldKey);
    const requestId = ++latestBlurRequestIdRef.current;
    const nextAnswers = { ...answersRef.current, [fieldKey]: nextValue };
    hydrate(nextAnswers);

    try {
      const data = await adapter.validate(stripClientOnlyFields(nextAnswers), grantToken);
      if (requestId !== latestBlurRequestIdRef.current) return;

      if (data?.validation) setServerValidation(data.validation);

      if (data?.form) {
        const serverAnswers = data.form.answers || nextAnswers;
        const localAnswers = { ...answersRef.current };
        const merged = { ...serverAnswers };
        for (const k of Object.keys(localAnswers)) {
          if (JSON.stringify(localAnswers[k]) !== JSON.stringify(nextAnswers[k])) {
            merged[k] = localAnswers[k];
          }
        }
        const mergedAnswers = normalizeRespondentAnswers(
          data.form.fields || form?.fields,
          merged,
        );
        setForm((prev) => ({ ...(prev || {}), ...data.form, answers: mergedAnswers }));
        hydrate(mergedAnswers);
        setError('');
      } else if (data?.status === 'VALIDATION_FAILED') {
        setError('Please fix the highlighted fields and try again.');
      } else {
        setError('');
      }

      lastValidatedFieldValueRef.current[fieldKey] = serializedValue;
    } catch (e) {
      if (requestId === latestBlurRequestIdRef.current) {
        setError(resolveErrorMessage(e, 'Unable to validate this field right now.'));
      }
    } finally {
      blurValidationInFlightRef.current.delete(fieldKey);
    }
  }, [
    adapter,
    grantToken,
    submitting,
    markFieldTouched,
    clearFieldValidation,
    requestServerAutoSave,
    answersRef,
    hydrate,
  ]);

  // Wrap the core saveDraft to layer standalone-specific post-processing on top:
  // server-side form merge, validation surfacing, instance notification, error state.
  const saveDraft = useCallback(async () => {
    if (!adapter.capabilities.canSaveDraft) return;
    try {
      const data = await coreSaveDraft();
      if (!data) return;

      if (data?.validation) setServerValidation(data.validation);

      if (data?.form) {
        const serverAnswers = data.form.answers || answersRef.current;
        const localAnswers = { ...answersRef.current };
        const merged = { ...serverAnswers };
        for (const k of Object.keys(localAnswers)) {
          if (JSON.stringify(localAnswers[k]) !== JSON.stringify(serverAnswers[k])) {
            merged[k] = localAnswers[k];
          }
        }
        const mergedAnswers = normalizeRespondentAnswers(
          data.form.fields || form?.fields,
          merged,
        );
        setForm((prev) => ({ ...(prev || {}), ...data.form, answers: mergedAnswers }));
        hydrate(mergedAnswers);
      } else if (data?.status === 'VALIDATION_FAILED') {
        setError('Please fix the highlighted fields and try again.');
      }
      if (data?.form || data?.status !== 'VALIDATION_FAILED') {
        setError('');
      }

      if (data?.instanceId && onInstanceCreated && !instanceNotifiedRef.current) {
        instanceNotifiedRef.current = true;
        onInstanceCreated(data.instanceId);
      }
    } catch (e) {
      setError(resolveErrorMessage(e, 'Failed to save draft.'));
    }
  }, [adapter, coreSaveDraft, onInstanceCreated, answersRef, hydrate]);

  const submit = useCallback(async () => {
    setShowAllValidation(true);

    // answersRef is the authoritative snapshot — React state may lag a final keystroke
    // that happened between onChange and the submit click.
    const latestAnswers = answersRef.current;
    const submitAnswers = buildVisibleAnswerPayload(form?.fields, latestAnswers);

    if (hasPendingUploads(submitAnswers)) {
      setError(PENDING_UPLOAD_MESSAGE);
      return;
    }

    const clientErrors = clientValidate(form?.fields, submitAnswers);
    if (Object.keys(clientErrors).length > 0) return;

    // Kill any pending debounced autosave — submit's pre-save flushes the latest answers
    // anyway and a late-firing autosave could race against the submit (or against the
    // next-step componentId in flow mode).
    cancelPendingServerAutoSave();
    setSubmitting(true);
    try {
      // Pre-save: flush answers before submit so a failed /submit still leaves a
      // persisted draft the respondent can come back to.
      if (adapter.capabilities.canSaveDraft) {
        try {
          const preSave = await adapter.saveDraft(stripClientOnlyFields(submitAnswers), grantToken);
          if (
            preSave?.instanceId &&
            onInstanceCreated &&
            !instanceNotifiedRef.current
          ) {
            instanceNotifiedRef.current = true;
            onInstanceCreated(preSave.instanceId);
          }
        } catch {
          // Non-fatal — backend /submit also persists answers. Proceed.
        }
      }

      const data = await adapter.submit(stripClientOnlyFields(submitAnswers), grantToken);
      if (data?.validation) setServerValidation(data.validation);

      if (data?.instanceId && onInstanceCreated && !instanceNotifiedRef.current) {
        instanceNotifiedRef.current = true;
        onInstanceCreated(data.instanceId);
      }

      // Cross-component handoff takes precedence (but not over validation).
      if (data?.status !== 'VALIDATION_FAILED' && handleHandoffEnvelope(data)) {
        clearLocalBackup();
        return;
      }

      if (data?.status === 'VALIDATION_FAILED') {
        setError('Please fix the highlighted fields and try again.');
      } else if (adapter.capabilities.showConfirmationOnSubmit) {
        setSubmitted(true);
        setError('');
        clearLocalBackup();
      } else if (data?.form) {
        const serverAnswers = data.form.answers || submitAnswers;
        const localAnswers = { ...answersRef.current };
        const merged = { ...serverAnswers };
        for (const k of Object.keys(localAnswers)) {
          if (JSON.stringify(localAnswers[k]) !== JSON.stringify(submitAnswers[k])) {
            merged[k] = localAnswers[k];
          }
        }
        const mergedAnswers = normalizeRespondentAnswers(
          data.form.fields || form?.fields,
          merged,
        );
        setForm((prev) => ({ ...(prev || {}), ...data.form, answers: mergedAnswers }));
        hydrate(mergedAnswers);
        setError('');
        clearLocalBackup();
      } else {
        setLoading(true);
        await loadForm(false);
        setError('');
        clearLocalBackup();
      }
    } catch (e) {
      setError(resolveErrorMessage(e, 'Failed to submit form.'));
    } finally {
      setSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adapter, form, grantToken, onInstanceCreated, clearLocalBackup, cancelPendingServerAutoSave]);

  const refresh = useCallback(async () => {
    if (!adapter.capabilities.canRefresh || submitting) return;
    setLoading(true);
    await loadForm(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adapter, submitting, grantToken]);

  const clientErrors = useMemo(
    () => clientValidate(form?.fields, answers),
    [form?.fields, answers]
  );

  const filteredValidation = useMemo(() => {
    const mergedErrors = { ...clientErrors, ...(serverValidation.errors || {}) };
    const mergedWarnings = { ...(serverValidation.warnings || {}) };

    if (showAllValidation) return { errors: mergedErrors, warnings: mergedWarnings };

    const visibleKeys = new Set(Object.keys(touchedFields).filter((key) => touchedFields[key]));
    const filterBucket = (bucket = {}) =>
      Object.fromEntries(Object.entries(bucket).filter(([key]) => visibleKeys.has(key)));

    return {
      errors: filterBucket(mergedErrors),
      warnings: filterBucket(mergedWarnings),
    };
  }, [showAllValidation, touchedFields, clientErrors, serverValidation]);

  const capabilities = useMemo(() => adapter.capabilities, [adapter, form]);
  const hasPendingUploadAnswers = useMemo(() => hasPendingUploads(answers), [answers]);

  const readOnly = adapter.deriveReadOnly(form);
  const canSubmit = adapter.deriveCanSubmit(form) && !hasPendingUploadAnswers;
  const canEdit = adapter.deriveCanEdit(form);
  const coverImageUrl = form?.formSettings?.coverImageUrl || undefined;

  const requestMagicLink = useCallback(
    async (email) => adapter.requestMagicLink(email),
    [adapter]
  );

  const onSignedIn = useCallback(() => {
    setAuthPassed(true);
  }, []);

  return {
    loading,
    submitting,
    savingDraft,
    form,
    answers,
    validation: filteredValidation,
    error,
    gateState,
    submitted,
    readOnly,
    canSubmit,
    canEdit,
    coverImageUrl,
    capabilities,
    autoSaveStatus,
    lastSavedAt,
    onChangeAnswer,
    onFieldBlur,
    onUpload,
    onRemoveFile,
    retryUpload,
    saveDraft,
    submit,
    refresh,
    requestMagicLink,
    onSignedIn,
  };
}
